import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';

const PAGE_SIZE = 20;
const INBOX_STATUS_UNREAD = 0;

const TABS = [
  { key: 'inbox', labelKey: 'messages.tabInbox' },
  { key: 'announcement', labelKey: 'messages.tabAnnouncement' },
];

export default function MessagesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { locale, t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => (isAuthenticated ? 'inbox' : 'announcement'));
  const [inboxRecords, setInboxRecords] = useState([]);
  const [announcementRecords, setAnnouncementRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readAllLoading, setReadAllLoading] = useState(false);

  const visibleTabs = isAuthenticated
    ? TABS
    : TABS.filter((tab) => tab.key === 'announcement');

  useEffect(() => {
    if (!isAuthenticated && activeTab === 'inbox') {
      setActiveTab('announcement');
      setPage(1);
    }
  }, [activeTab, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const apiLang = resolveNewsApiLang(locale);
        if (activeTab === 'inbox' && isAuthenticated) {
          const data = await memberApi.inboxPageQuery({
            pageNo: page,
            pageSize: PAGE_SIZE,
            lang: apiLang,
          });
          if (!cancelled) {
            setInboxRecords(data?.records || []);
            setTotalPages(data?.pages || 1);
          }
        } else {
          const data = await memberApi.announcementCenterPageQuery({
            pageNo: page,
            pageSize: PAGE_SIZE,
            lang: apiLang,
          });
          if (!cancelled) {
            setAnnouncementRecords(data?.records || []);
            setTotalPages(data?.pages || 1);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('messages.loadFailed'));
          if (activeTab === 'inbox') {
            setInboxRecords([]);
          } else {
            setAnnouncementRecords([]);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activeTab, isAuthenticated, locale, page, t]);

  function handleTabChange(tabKey) {
    setActiveTab(tabKey);
    setPage(1);
    setError('');
  }

  async function handleReadAll() {
    if (activeTab !== 'inbox' || readAllLoading) {
      return;
    }
    setReadAllLoading(true);
    setError('');
    try {
      await memberApi.inboxReadAll();
      setPage(1);
      const data = await memberApi.inboxPageQuery({
        pageNo: 1,
        pageSize: PAGE_SIZE,
        lang: resolveNewsApiLang(locale),
      });
      setInboxRecords(data?.records || []);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || t('messages.loadFailed'));
    } finally {
      setReadAllLoading(false);
    }
  }

  const currentRecords = activeTab === 'inbox' ? inboxRecords : announcementRecords;
  const showEmpty = !loading && !error && currentRecords.length === 0;

  return (
    <div className="messages-page">
      <header className="messages-page__header">
        <button
          type="button"
          className="messages-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('messages.title')}</h1>
        <button
          type="button"
          className="messages-page__read-all"
          disabled={!isAuthenticated || activeTab !== 'inbox' || readAllLoading}
          onClick={handleReadAll}
        >
          {readAllLoading ? t('common.loading') : t('messages.readAll')}
        </button>
      </header>

      <div className="messages-page__tabs">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`messages-page__tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {loading ? <p className="messages-page__status">{t('common.loading')}</p> : null}
      {error ? <p className="messages-page__status messages-page__status--error">{error}</p> : null}

      {showEmpty ? (
        <div className="messages-page__empty">
          <div className="messages-page__empty-icon" aria-hidden>
            💬
          </div>
          <p>{t('messages.empty')}</p>
        </div>
      ) : null}

      {!loading && !error && activeTab === 'inbox' && inboxRecords.length > 0 ? (
        <>
          <ul className="messages-inbox-list">
            {inboxRecords.map((item) => (
              <li key={item.id}>
                <Link to={`/messages/inbox/${item.id}`} className="messages-inbox-list__item">
                  <div className="messages-inbox-list__title-row">
                    {item.status === INBOX_STATUS_UNREAD ? (
                      <span className="messages-inbox-list__dot" aria-hidden />
                    ) : null}
                    <div className="messages-inbox-list__title">{item.title}</div>
                  </div>
                  <div className="messages-inbox-list__time">{formatDateTime(item.createTime)}</div>
                </Link>
              </li>
            ))}
          </ul>
          {totalPages > 1 ? (
            <div className="messages-announcement-pagination">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('messages.prevPage')}
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('messages.nextPage')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {!loading && !error && activeTab === 'announcement' && announcementRecords.length > 0 ? (
        <>
          <ul className="messages-announcement-list">
            {announcementRecords.map((item) => (
              <li key={item.id}>
                <Link to={`/messages/announcements/${item.id}`} className="messages-announcement-list__item">
                  <div className="messages-announcement-list__title">{item.title}</div>
                  <div className="messages-announcement-list__time">{formatDateTime(item.createTime)}</div>
                </Link>
              </li>
            ))}
          </ul>
          {totalPages > 1 ? (
            <div className="messages-announcement-pagination">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('messages.prevPage')}
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('messages.nextPage')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
